import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { fmtINR } from "@/lib/api";
import SectionHeader from "@/components/layout/SectionHeader";
import { Star, ArrowRight, MapPin } from "lucide-react";

export function HotelCard({ hotel }) {
  return (
    <a
      href={hotel.affiliate_url}
      target="_blank"
      rel="noopener sponsored"
      data-testid={`hotel-card-${hotel.name.toLowerCase().replace(/\s+/g, "-")}`}
      className="group block bg-white rounded-2xl overflow-hidden border border-slate-200 card-hover"
    >
      <div className="relative h-56 overflow-hidden">
        <img src={hotel.image} alt={hotel.name} className="w-full h-full object-cover img-zoom" loading="lazy" />
        <span className="absolute top-3 left-3 px-2.5 py-1 text-[10px] uppercase tracking-widest font-bold rounded bg-white/95 text-brand">
          {hotel.category}
        </span>
      </div>
      <div className="p-5">
        <div className="flex items-center gap-1.5 text-xs text-slate-500">
          <Star size={12} className="text-accent fill-accent" />
          <span className="font-semibold text-slate-700">{hotel.rating}</span>
          <span>· {hotel.reviews?.toLocaleString?.()} reviews</span>
        </div>
        <h3 className="mt-1 font-montserrat font-bold text-lg text-slate-900">{hotel.name}</h3>
        <div className="text-sm text-slate-500 inline-flex items-center gap-1 mt-0.5"><MapPin size={12} /> {hotel.location}</div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {(hotel.perks || []).slice(0, 3).map((p) => (
            <span key={p} className="text-[10px] uppercase tracking-wider font-semibold text-slate-600 bg-slate-100 px-2 py-1 rounded">{p}</span>
          ))}
        </div>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="text-[11px] text-slate-500">From</div>
            <div className="text-xl font-montserrat font-bold text-slate-900">{fmtINR(hotel.from_price)}</div>
            <div className="text-[10px] text-slate-500">per night · via {hotel.partner}</div>
          </div>
          <span className="inline-flex items-center gap-1 text-sm font-semibold text-brand group-hover:gap-2 transition-all">
            Book <ArrowRight size={14} />
          </span>
        </div>
      </div>
    </a>
  );
}

export default function FeaturedHotels() {
  const [hotels, setHotels] = useState([]);
  useEffect(() => { api.get("/hotels").then(({ data }) => setHotels(data)).catch(() => {}); }, []);
  if (!hotels.length) return null;

  // 1 per category
  const pick = (cat) => hotels.find((h) => h.category === cat);
  const featured = [pick("luxury"), pick("budget"), pick("family")].filter(Boolean);

  return (
    <section data-testid="featured-hotels" className="container-page py-16 md:py-24">
      <SectionHeader
        kicker="Featured Hotels"
        title="Stays for every kind of traveller"
        subtitle="From overwater villas to wallet-friendly hostels and family resorts — we've got a perfect stay for every story."
        cta={
          <Link to="/hotels" data-testid="see-all-hotels" className="text-sm font-semibold text-brand hover:underline inline-flex items-center gap-1 ml-auto">
            See all hotels <ArrowRight size={14} />
          </Link>
        }
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
        {featured.map((h) => <HotelCard key={h.id} hotel={h} />)}
      </div>
    </section>
  );
}
